import React, { useEffect, useState } from 'react';
import {
    Form,
    Button,
    Divider,
    Input,
    message,
    Modal,
    Spin,
    Typography,
    Avatar,
    Card,
    notification,
    Select,
    Checkbox,
} from 'antd';
import api from '../../utils/api';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import UserLogo from '../../assets/images/user_logo.png';
import { UserProps } from '@partsbadger/types';
import { SalesOrderProps } from '../../types/types';

import { FormErrorParser, UploadFile, ErrorRender } from '@partsbadger/library';

import type { CheckboxValueType } from 'antd/es/checkbox/Group';

interface ContactRepProps {
    current_user: UserProps;
    handleClose: () => void;
    emailSubject?: string;
    order?: SalesOrderProps;
}

const { Title, Paragraph, Text } = Typography;

const CheckboxGroup = Checkbox.Group;

const ContactRepForm = ({ current_user, emailSubject, handleClose, order }: ContactRepProps) => {
    const [showSpin, setShowSpin] = useState<boolean>(false);

    // Rep Inf Hooks
    const [execName, setExecName] = useState<string>();
    const [execBio, setExecBio] = useState<string | null>();
    const [execEmail, setExecEmail] = useState<string>();
    const [execImgUrl, setExecImgUrl] = useState<string>(UserLogo);
    const [checkedList, setCheckedList] = useState<CheckboxValueType[]>();

    const [form] = Form.useForm();

    const RepInfo = {
        Name: execName,
        PhotoUrl: execImgUrl,
        Bio: execBio,
        Email: execEmail,
    };

    useEffect(() => {
        if (current_user?.default_contact?.account_owner) {
            setExecBio(current_user.default_contact.account_owner.biography);
            setExecName(current_user.default_contact.account_owner.fullname);
            setExecEmail(current_user.default_contact.account_owner.email);

            if (current_user.default_contact.account_owner.picture) {
                setExecImgUrl(current_user.default_contact.account_owner.picture);
            }
        }

        if (emailSubject) {
            form.setFieldsValue({ subject: `Update on order No. ${emailSubject}` });
        }
    }, []);

    const onSubmit = async () => {
        form.validateFields()
            .then(async values => {
                setShowSpin(true);
                if (values.message?.length < 5 || values.message?.length > 200) {
                    notification.error({
                        message:
                            'Please note that the minimum character limit is 5, while the maximum allowed is 200 characters.',
                        placement: 'topRight',
                    });
                    setShowSpin(false);
                    return true;
                }

                const getFileWithDoneStatus = values?.files?.filter((item: any) => {
                    return item?.status == 'done';
                });

                if (values.contacting_for === 'quality_issue' && getFileWithDoneStatus.length <= 0) {
                    notification.success({
                        message: 'Please make sure to upload a valid file.',
                        placement: 'topRight',
                    });
                }

                if (values.contacting_for === 'quality_issue' && !getFileWithDoneStatus?.length) {
                    notification.error({
                        message: 'Please upload the files again',

                        description:
                            'Apologies, but it seems there was an issue with your file upload. Please try uploading the files once more.',
                        placement: 'topRight',
                    });
                    setShowSpin(false);
                    return true;
                }

                const requestData = {
                    username: current_user.fullname,
                    subject: values.subject,
                    message: values.message,
                    email: current_user.email,
                    contacting_for: values.contacting_for ? values.contacting_for : 'general',
                    sales_order_id: order?.id,
                    line_items: values?.line_items,
                    files:
                        getFileWithDoneStatus?.length >= 1
                            ? getFileWithDoneStatus?.map((item: any) => {
                                  return item.response;
                              })
                            : null,
                };

                await api.Orders.contactRep(requestData)
                    .then(() => {
                        setShowSpin(false);
                        handleClose();

                        notification.success({
                            message: 'Your request has been sent successfully',
                            placement: 'topRight',
                        });
                    })
                    .catch(error => {
                        setShowSpin(false);
                        notification.error({
                            message: <ErrorRender error={error} />,
                            placement: 'topRight',
                        });
                    });
            })
            .catch(errorInfo => {
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Please input all required fields',
                    description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                    placement: 'top',
                    maxCount: 1,
                });
            });
    };

    const selectOptions =
        order?.status == 'Shipped in Full' ||
        order?.status == 'Shipped & Paid' ||
        order?.status === 'Shipped in Partial'
            ? [
                  {
                      value: 'order_update',
                      label: 'Request an Order Update',
                      disabled: order?.status == 'Shipped in Full' || order?.status == 'Shipped & Paid' ? true : false,
                  },
                  {
                      value: 'submit_feedback',
                      label: 'Submit Feedback',
                  },
                  {
                      value: 'quality_issue',
                      label: 'Submit Quality Issue',
                  },
              ]
            : [
                  {
                      value: 'order_update',
                      label: 'Request an Order Update',
                  },
              ];

    const plainOptions = order?.line_items.map(item => {
        return { value: item.id, label: item.name };
    });

    const onChange = (list: CheckboxValueType[]) => {
        setCheckedList(list);
    };

    return (
        <Modal
            title={
                <Title level={3} className="mb-0">
                    {order?.status == 'Shipped in Full' || order?.status == 'Shipped & Paid'
                        ? 'Requesting Support on Order No. ' + order?.id
                        : 'Contact Your PartsBadger Representative'}
                </Title>
            }
            open={true}
            footer={null}
            onCancel={handleClose}
        >
            <Spin spinning={showSpin}>
                <Form
                    layout="vertical"
                    onFinish={onSubmit}
                    form={form}
                    onFinishFailed={errorInfo => {
                        notification.error({
                            key: 'form-errors',
                            duration: 8,
                            message: 'Please input all required fields',
                            description: <FormErrorParser isAntd4 errors={errorInfo.errorFields} />,
                            placement: 'top',
                            maxCount: 1,
                        });
                    }}
                >
                    <Paragraph>
                        Please use the form below to describe your inquiry. Your account manager will contact you as
                        soon as possible to help with your needs.
                    </Paragraph>

                    <Divider />

                    {RepInfo.Name ? (
                        <>
                            <Title level={4}>Your PB Representative&apos;s Info:</Title>

                            <Card className="shadow-sm">
                                <div className="d-flex flex-row">
                                    <Avatar alt="sales-representative" src={RepInfo.PhotoUrl} />

                                    <div className="ml-2">
                                        <Text strong className="mb-0 d-block">
                                            {RepInfo.Name}
                                        </Text>
                                        <Text className="d-block">{RepInfo.Email}</Text>
                                    </div>
                                </div>
                            </Card>

                            <Divider />
                        </>
                    ) : null}

                    <Form.Item
                        name="subject"
                        label="Subject"
                        rules={[{ required: true, message: 'Please input a subject' }]}
                    >
                        <Input type="text" placeholder="Type your subject here..." />
                    </Form.Item>

                    {order && (
                        <Form.Item
                            name="contacting_for"
                            label="Contacting For"
                            rules={[{ required: true, message: 'Select an option under "Contact for"' }]}
                        >
                            <Select placeholder="Select an option..." options={selectOptions}></Select>
                        </Form.Item>
                    )}

                    <Form.Item noStyle dependencies={['contacting_for']}>
                        {({ getFieldValue }) => {
                            return (
                                getFieldValue('contacting_for') === 'quality_issue' && (
                                    <>
                                        <Divider />
                                        <p>
                                            Please identify any products that have quality issues and provide the
                                            necessary documentation by uploading it to the system.
                                        </p>
                                        <Form.Item
                                            name="line_items"
                                            label="Products"
                                            rules={[{ required: true, message: 'Select one option' }]}
                                            style={{ maxHeight: '90px', overflow: 'auto' }}
                                        >
                                            <CheckboxGroup
                                                options={plainOptions}
                                                style={{ display: 'flex', flexDirection: 'column' }}
                                            />
                                        </Form.Item>

                                        <Form.Item
                                            label="Upload an Inspection / Quality documentation"
                                            // {...formItemLayout}
                                            name="files"
                                            rules={[{ required: true, message: 'Please input a file.' }]}
                                        >
                                            <UploadFile multiple={true} maxCount={10} maxSize={20} />
                                        </Form.Item>
                                        <Divider />
                                    </>
                                )
                            );
                        }}
                    </Form.Item>

                    <Form.Item
                        name="message"
                        label="Message"
                        rules={[{ required: true, message: 'Please type a message' }]}
                    >
                        <Input.TextArea
                            placeholder="Type your message here..."
                            rows={4}
                            minLength={5}
                            maxLength={200}
                        />
                    </Form.Item>

                    <div className="d-flex flex-column">
                        <Button id="contact-rep-send-msg" className="w-100 mb-2" htmlType="submit" type="primary">
                            Send Message
                        </Button>
                        <Button onClick={handleClose} className="w-100" htmlType="button">
                            Cancel
                        </Button>
                    </div>
                </Form>
            </Spin>
        </Modal>
    );
};

export default ContactRepForm;
